import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api'
import type { DashboardResponse } from './useDashboard'

export interface IntakeData {
  // Step 2 — About you
  display_name?: string | null
  age?: number | null
  pronouns?: string | null
  pronoun_other?: string | null

  // Step 3 — Your story
  conditions?: string[]
  pain_duration?: string | null
  something_else_detail?: string | null
  primary_diagnosis?: string | null

  // Step 4 — Your cycle
  is_pregnant?: string | null
  menarche_age?: string | number | null
  cycle_length?: string | null
  cycle_length_days?: number | null
  period_duration?: string | null

  // Step 5 — Recent periods
  lmp_dates?: string[]
  lmp_ranges?: { start: string; end: string }[]
  last_period_start?: string | null

  // Step 4 — heavy-bleeding treatment history (anemia management)
  iv_iron?: string | null
  blood_transfusions?: string | null

  // Step 6 — Mood & wellbeing (validated screeners). Responses are per-item
  // 0-3 frequency codes; null = unanswered. Scores are the summed totals
  // (PHQ-9: 0-27, GAD-7: 0-21) computed on save.
  phq9_responses?: (number | null)[]
  phq9_score?: number | null
  gad7_responses?: (number | null)[]
  gad7_score?: number | null

  // Control
  completed_step?: number
  intake_completed?: boolean
}

interface IntakeResponse {
  intake: (IntakeData & { intake_completed?: boolean; completed_step?: number }) | null
}

export function useIntake() {
  return useQuery({
    queryKey: ['patient', 'intake'],
    queryFn: () => api.get<IntakeResponse>('/api/patient/intake'),
    staleTime: 5 * 60_000,
  })
}

export function useSaveIntake() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: IntakeData) => api.post('/api/patient/intake', data),
    onSuccess: (_res, variables) => {
      // When this save *completes* intake, seed the completed flag into the
      // intake + dashboard caches before the background refetch confirms it.
      // Both redirect guards read cached query data: PatientLayout redirects to
      // /patient/intake while dashboard.intake_completed is false, and IntakePage
      // redirects to /patient while intake.intake_completed is true. The dashboard
      // query is inactive on the intake route, so a plain invalidate only marks it
      // stale — its cached value stays false until a refetch lands. Navigating to
      // /patient in that window makes the two routes ping-pong (the intake flashes
      // a few times) until the refetch resolves. Writing the truth now closes that
      // window so the guards agree immediately.
      if (variables.intake_completed) {
        queryClient.setQueryData<IntakeResponse>(['patient', 'intake'], (old) =>
          old?.intake ? { intake: { ...old.intake, intake_completed: true } } : old,
        )
        queryClient.setQueriesData<DashboardResponse>(
          { queryKey: ['patient', 'dashboard'] },
          (old) => (old ? { ...old, intake_completed: true } : old),
        )
      }
      queryClient.invalidateQueries({ queryKey: ['patient', 'intake'] })
      queryClient.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
    },
  })
}
